import { useQuery } from '@tanstack/react-query';

import { getCreditAcquisitions } from 'apis/restaurant-api/merchant-admin/credits/api';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { useFeatureFlags } from 'common/hooks';
import useMerchantExperiments from 'common/hooks/useMerchantExperiments';
import useUser from 'common/hooks/useUser';

import { getAvailableOfferForCurrency } from './utils';

const FEATURE_FLAG_NAME = 'merchant-reviews-compensation-offers';
const EXPERIMENT_NAME = 'maxx_insights_v2_consumer_rating_concessions';
const TEST_GROUP_NAME = 'test_enable_rating_concessions';

export const useIsOffersEnabled = (venue: Venue) => {
  const { user } = useUser();

  const {
    data: { [FEATURE_FLAG_NAME]: featureFlagAssignment = '' } = {},
    error: featureFlagError,
    isLoading: isLoadingFeatureFlag,
  } = useFeatureFlags([FEATURE_FLAG_NAME], {
    user_id: user?.id ?? '',
    venue_country: venue.country,
  });

  const {
    data: { [EXPERIMENT_NAME]: experimentAssignment = '' } = {},
    error: experimentError,
    isLoading: isLoadingExperiment,
  } = useMerchantExperiments<typeof EXPERIMENT_NAME, typeof TEST_GROUP_NAME>([EXPERIMENT_NAME], {
    venue_id: venue.id,
    merchant_id: venue.merchant,
    country: venue.country,
  });

  const isEnabledByRegattaAssignment =
    (!featureFlagError && featureFlagAssignment === 'on') ||
    (!experimentError && experimentAssignment === TEST_GROUP_NAME);

  const availableOffer = getAvailableOfferForCurrency(venue.currency);

  return {
    isOffersEnabled: !!availableOffer && isEnabledByRegattaAssignment,
    isLoading: (isLoadingFeatureFlag || isLoadingExperiment) && !isEnabledByRegattaAssignment,
  };
};

export const useSentOffers = ({
  venueId,
  purchaseIds,
  isEnabled,
}: {
  venueId: string;
  purchaseIds: string[];
  isEnabled: boolean;
}) => {
  return useQuery(
    ['credit-acquisitions', venueId, purchaseIds],
    () => {
      if (!isEnabled) {
        throw new Error('Credit acquisitions are not supported');
      }

      return getCreditAcquisitions(venueId, purchaseIds);
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      enabled: isEnabled && !!purchaseIds.length,
    },
  );
};
